import { useMemo, useState, useEffect } from "react";
import { useInfiniteListSavedSearches } from "../hooks/useInfiniteListSavedSearches";
import { useSavedSearches } from "../hooks/useSavedSearches";
import MultiSelectCombobox from "src/components/core/MultiSelectComboBox";
import { ComboBoxListOption } from "src/components/core/ComboBox";
import { useQueryClient } from "@tanstack/react-query";
import { SavedSearch } from "src/types/neighborhoodSearch";
import { NS_SAVED_SEARCH_CACHE_KEY } from "../constants/cacheKeys";

export const SavedSearchesSelector = ({
    selectedItems,
    onSelect,
    onRemove,
    onSelectedSearchesChange,
}: {
    selectedItems: ComboBoxListOption[];
    onSelect: (selected: ComboBoxListOption) => void;
    onRemove: (removed: ComboBoxListOption) => void;
    onSelectedSearchesChange?: (selectedSearches: SavedSearch[]) => void;
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const queryClient = useQueryClient();

  const {
    data: savedSearchesPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteListSavedSearches({
    search: searchTerm,
  });
  
  // Get all selected IDs
  const selectedIds = useMemo(() => 
    selectedItems.map(item => item.value),
    [selectedItems]
  );

  // Use a stable key for the query to ensure proper refetching
  const queryKey = useMemo(() => 
    selectedIds.sort().join(','), 
    [selectedIds]
  );

  const { data: selectedSearches } = useSavedSearches(queryKey ? selectedIds : []);

  // Call onSelectedSearchesChange when selectedSearches changes
  useEffect(() => {
    if (selectedSearches && onSelectedSearchesChange) {
      onSelectedSearchesChange(selectedSearches);
    }
  }, [selectedSearches, onSelectedSearchesChange]);

  const savedSearchesOptions = useMemo(() => {
    const serverItems = savedSearchesPages?.pages.flatMap((page) =>
      page.map(({ name, id }) => ({
        label: name,
        value: id,
      } as ComboBoxListOption))
    ) || [];

    // Add any selected items that weren't in the main list
    const additionalSelectedItems = selectedSearches?.map(({ name, id }) => ({
      label: name,
      value: id,
    } as ComboBoxListOption)) || [];

    // Filter out any items that are already in serverItems
    const uniqueAdditionalItems = additionalSelectedItems.filter(
      additionalItem => !serverItems.some(serverItem => serverItem.value === additionalItem.value)
    );

    return [...uniqueAdditionalItems, ...serverItems];
  }, [selectedSearches, savedSearchesPages]);

  const handleSelect = (item: ComboBoxListOption) => {
    onSelect(item);
    // Invalidate the saved search queries to trigger a refetch
    queryClient.invalidateQueries({
      queryKey: [NS_SAVED_SEARCH_CACHE_KEY],
    });
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    // Load more when user scrolls to bottom (with 20px threshold)
    if (scrollHeight - scrollTop - clientHeight < 20 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <MultiSelectCombobox
      suppressSelectedBadges
      list={savedSearchesOptions}
      value={selectedItems}
      onRemove={onRemove}
      onSelect={handleSelect}
      placeholder="Select saved searches"
      onScroll={handleScroll}
      isLoading={isFetchingNextPage}
      onSearch={setSearchTerm}
    />
  );
};
